import { Component, OnInit, ViewChild, AfterViewInit, ElementRef, ChangeDetectorRef } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { fromEvent, merge } from "rxjs";
import { debounceTime, distinctUntilChanged, tap } from "rxjs/operators";
import { AddEditFireworkTypesComponent } from './add-edit-firework-types/add-edit-firework-types.component';
import { FireworkTypesDataSource } from "../services/data-sources/firework-types.datasource";
import { FireworkTypesService } from "../services/firework-types.service";
import { ConfirmPopupComponent } from "../common/confirm-popup/confirm-popup.component";
import { LanguagesService } from "../services/languages.service";
import { Language } from "../interfaces/language.interface";
import { SelectionModel } from "@angular/cdk/collections";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AuthService } from '../services/auth/auth.service';

@Component({
    selector: 'app-firework-types',
    templateUrl: './firework-types.component.html',
    styleUrls: ['./firework-types.component.scss'],
    standalone: false
})
export class FireworkTypesComponent implements OnInit, AfterViewInit {

	languages: Language[] = [];
	displayedColumns: string[] = ["sn", "select", "id"];
	selection = new SelectionModel<number>(true, []);
	bulkAction = '';
	dataSource!: FireworkTypesDataSource;
	currentPageCount = 0;
	count = 0;
	authUser: any;

	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;
	@ViewChild('search') search!: ElementRef;

	constructor(
		private fireworkTypesService: FireworkTypesService,
		public dialog: MatDialog, public authService: AuthService,
		private languageService: LanguagesService,
		private _snackBar: MatSnackBar,
		private ref: ChangeDetectorRef
	) {
		this.languageService.index().subscribe(languages => {
			this.languages = languages;
			this.languages.forEach(language => {
				this.displayedColumns.push('language_id_' + language.id);
			})
			this.displayedColumns.push('actions');
		})
		this.dataSource = new FireworkTypesDataSource(this.fireworkTypesService);

		this.authService.user.subscribe((res: any) => {
			this.authUser = res.user;
		});
	}
	ngOnInit() {
		this.dataSource.currentPageCount$.subscribe(currentPageCount => {
			this.currentPageCount = currentPageCount;
		});
		this.dataSource.count$.subscribe(count => {
			this.count = count;
		});
	}
	ngAfterViewInit() {
		fromEvent(this.search.nativeElement, 'keyup')
			.pipe(
				debounceTime(150),
				distinctUntilChanged(),
				tap(() => {
					this.paginator.pageIndex = 0;

					this.loadPage();
				})
			)
			.subscribe();

		merge(this.sort.sortChange, this.paginator.page)
			.pipe(
				tap(() => this.loadPage())
			)
			.subscribe();
		this.sort.sort(({ id: 'id', start: 'desc' }) as MatSortable);
		this.ref.detectChanges();
	}

	/** Whether the number of selected elements matches the total number of rows. */
	isAllSelected() {
		return this.selection.selected.length === this.currentPageCount;
	}

	/** Selects all rows if they are not all selected; otherwise clear selection. */
	masterToggle() {
		this.isAllSelected() ?
			this.selection.clear() :
			this.dataSource.getList().forEach(row => this.selection.select(row.id));
	}

	getLanguageTranslation(translations: any, language_id: any) {
		return translations.filter((translation: any) => translation.language_id === language_id)[0]?.name;
	}

	loadPage() {
		this.selection.clear();
		this.dataSource.list(
			{
				search: this.search.nativeElement.value,
			},
			this.sort.active,
			this.sort.direction,
			this.paginator.pageIndex + 1,
			this.paginator.pageSize);
	}
	addEdit(id: number | undefined = undefined) {
		let options;
		if (id) {
			const fireworkType = this.dataSource.getList().find(x => x.id == id);
			options = {
				maxWidth: '767px',
				width: '100%',
				data: {
					id: 'edit-firework-type',
					fireworkType: fireworkType,
					languages: this.languages
				}
			}
		}
		else options = {
			maxWidth: '767px',
			width: '100%',
			data: {
				id: 'add-firework-type',
				languages: this.languages
			}
		}
		const dialogRef = this.dialog.open(AddEditFireworkTypesComponent, options);

		dialogRef.afterClosed().subscribe(result => {
			if (result) this.loadPage();
		});
	}
	delete(id: number) {
		const dialogRef = this.dialog.open(ConfirmPopupComponent, {
			id: 'delete-record',
			maxWidth: '480px',
			width: '100%',
			data: {
				title: 'Confirmation',
				message: 'Are you sure you want to delete this firework type?'
			}
		});

		dialogRef.componentInstance.onConfirm.subscribe(() => {
			this.fireworkTypesService.destroy(id).subscribe(() => {
				dialogRef.close()
				this.loadPage();
			});
		});
	}
	bulkActionConfirm() {
		if (this.bulkAction === '') {
			return;
		}
		if (this.selection.selected.length === 0) {
			this._snackBar.open('Select some records to perform this action.', 'Dismiss');
			setTimeout(() => {
				this.bulkAction = '';
			}, 500)
			return;
		}
		const dialogRef = this.dialog.open(ConfirmPopupComponent, {
			id: 'bulk-action',
			maxWidth: '480px',
			width: '100%',
			data: {
				title: 'Confirmation',
				message: 'Are you sure you want to ' + (
					this.bulkAction === 'delete' ? 'delete' : ''
				) + ' selected records ?'
			}
		});

		dialogRef.componentInstance.onConfirm.subscribe(() => {
			this.fireworkTypesService.bulkDelete(this.selection.selected).subscribe(() => {
				dialogRef.close();
				this.selection.clear();
				this.loadPage();
			});
		});
		dialogRef.afterClosed().subscribe(() => {
			this.bulkAction = '';
		});
	}
}
